import { useState, useCallback } from "react";
import JsonTree from "./JsonTree";
import "./JsonFormatter.css";

const SAMPLE_JSON = JSON.stringify(
  {
    name: "JSON 格式化工具",
    version: "1.0.0",
    description: "一个简单易用的 JSON 格式化与结构化查看工具",
    features: ["格式化", "压缩", "语法验证", "结构化展示"],
    author: {
      name: "Developer",
      email: "dev@example.com",
    },
    stats: {
      users: 1024,
      rating: 4.8,
      isActive: true,
      tags: ["tool", "json", "react"],
    },
    metadata: null,
  },
  null,
  2
);

type StatusType = "success" | "error" | "";

export default function JsonFormatter() {
  const [input, setInput] = useState("");
  const [parsed, setParsed] = useState<unknown>(null);
  const [status, setStatus] = useState<StatusType>("");
  const [message, setMessage] = useState("");

  const handleFormat = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setStatus("error");
      setMessage("请输入 JSON 内容");
      setParsed(null);
      return;
    }
    try {
      const obj = JSON.parse(trimmed);
      setParsed(obj);
      setInput(JSON.stringify(obj, null, 2));
      setStatus("success");
      setMessage("✓ 格式化成功");
    } catch (e) {
      setStatus("error");
      setMessage("✗ JSON 格式错误: " + (e as Error).message);
      setParsed(null);
    }
  }, [input]);

  const handleMinify = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed) {
      setStatus("error");
      setMessage("请输入 JSON 内容");
      return;
    }
    try {
      const obj = JSON.parse(trimmed);
      setParsed(obj);
      setInput(JSON.stringify(obj));
      setStatus("success");
      setMessage("✓ 压缩成功");
    } catch (e) {
      setStatus("error");
      setMessage("✗ JSON 格式错误: " + (e as Error).message);
      setParsed(null);
    }
  }, [input]);

  const handleClear = useCallback(() => {
    setInput("");
    setParsed(null);
    setStatus("");
    setMessage("");
  }, []);

  const handleSample = useCallback(() => {
    setInput(SAMPLE_JSON);
    setParsed(null);
    setStatus("");
    setMessage("");
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
      setStatus("");
      setMessage("");
    },
    []
  );

  return (
    <div className="formatter">
      <div className="toolbar">
        <button className="btn btn-primary" onClick={handleFormat}>
          <span className="btn-icon">&#x21BB;</span> 格式化
        </button>
        <button className="btn btn-secondary" onClick={handleMinify}>
          <span className="btn-icon">&#x2195;</span> 压缩
        </button>
        <button className="btn btn-sample" onClick={handleSample}>
          <span className="btn-icon">&#x1F4C4;</span> 示例
        </button>
        <button className="btn btn-danger" onClick={handleClear}>
          <span className="btn-icon">&#x2716;</span> 清空
        </button>
        {message && (
          <span className={`status-message status-${status}`}>{message}</span>
        )}
      </div>

      <div className="panels">
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">JSON 输入</span>
          </div>
          <textarea
            className="input-area"
            placeholder="在此粘贴或输入 JSON 内容..."
            value={input}
            onChange={handleInputChange}
            spellCheck={false}
          />
        </div>

        <div className="panel">
          <div className="panel-header">
            <span className="panel-title">结构化展示</span>
          </div>
          <div className="output-area">
            {parsed !== null ? (
              <JsonTree data={parsed} />
            ) : (
              <div className="placeholder">
                <div className="placeholder-icon">&#x1F4CB;</div>
                <p>输入 JSON 后点击"格式化"查看结构化展示</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
